<template>
  <v-card class="shops-table">
    <v-card-title class="shops-table__title">Shops im Vergleich</v-card-title>

    <!-- Mobile: kompakte Aufklapp-Zeilen statt gestapelter Riesen-Tabelle -->
    <v-expansion-panels v-if="$vuetify.breakpoint.mobile" flat accordion class="shops-table__mobile">
      <v-expansion-panel v-for="item in rows" :key="item.shopCode">
        <v-expansion-panel-header class="shops-table__mobile-header">
          <div class="shops-table__mobile-row">
            <span class="shops-table__mobile-name">
              <span class="shops-table__dot" :style="{ background: item.color }" />
              {{ item.domain }}
            </span>
            <span class="shops-table__mobile-stats">
              <span class="num">{{ formatEuro(item.revenue) }}</span>
              <span class="num" :class="item.profit < 0 ? 'num--bad' : 'num--good'">{{ formatEuro(item.profit) }}</span>
              <span class="num shops-table__mobile-margin">{{ item.margin.toFixed(1).replace(".", ",") }} %</span>
            </span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="shops-table__mobile-content">
          <ShopStatisticCard
            :shop-statistic="item.raw"
            :comparison-shop-statistic="comparisonForShop(item.shopCode)"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-data-table
      v-else
      :headers="headers"
      :items="rows"
      item-key="shopCode"
      :expanded.sync="expanded"
      show-expand
      single-expand
      hide-default-footer
      :items-per-page="-1"
      sort-by="revenue"
      sort-desc
      dense
    >
      <template v-slot:item.domain="{ item }">
        <span class="shops-table__dot" :style="{ background: item.color }" />
        <span class="shops-table__name">{{ item.domain }}</span>
      </template>
      <template v-slot:item.revenue="{ item }">
        <span class="num">{{ formatEuro(item.revenue) }}</span>
      </template>
      <template v-slot:item.costs="{ item }">
        <span class="num">{{ formatEuro(item.costs) }}</span>
      </template>
      <template v-slot:item.profit="{ item }">
        <span class="num" :class="item.profit < 0 ? 'num--bad' : 'num--good'">{{ formatEuro(item.profit) }}</span>
      </template>
      <template v-slot:item.margin="{ item }">
        <span class="num">{{ item.margin.toFixed(1).replace(".", ",") }} %</span>
      </template>
      <template v-slot:item.roas="{ item }">
        <span class="num">{{ item.roas ? item.roas.toFixed(2).replace(".", ",") : "—" }}</span>
      </template>
      <template v-slot:item.orders="{ item }">
        <span class="num">{{ item.orders }}</span>
      </template>
      <template v-slot:expanded-item="{ headers: tableHeaders, item }">
        <td :colspan="tableHeaders.length" class="shops-table__expand">
          <ShopStatisticCard
            :shop-statistic="item.raw"
            :comparison-shop-statistic="comparisonForShop(item.shopCode)"
          />
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import ShopStatisticCard from "@/components/Statistics/StatisticByShops/ShopStatisticCard";
import { statisticsColors } from "@/config";
import { getDecimalNumberString } from "@/utils";

export default {
  name: "ShopsTable",
  components: { ShopStatisticCard },
  props: {
    data: { type: Array, default: () => [] },
    comparisonData: { type: Array, default: null },
  },
  data() {
    return {
      expanded: [],
      headers: [
        { text: "Shop", value: "domain" },
        { text: "Umsatz", value: "revenue", align: "end" },
        { text: "Kosten", value: "costs", align: "end" },
        { text: "Gewinn", value: "profit", align: "end" },
        { text: "Marge", value: "margin", align: "end" },
        { text: "ROAS", value: "roas", align: "end" },
        { text: "Orders", value: "orders", align: "end" },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    rows() {
      return this.data.map((shop) => {
        // Nur numerische Kostenfelder summieren — schützt vor Map-Feldern
        // (z. B. byCountry in Alt-Sync-Docs), die sonst als "[object Object]"
        // in die Summe konkateniert würden
        const costs = Object.keys(shop.costs || {}).reduce(
          (sum, key) => sum + (typeof shop.costs[key] === "number" ? shop.costs[key] : 0), 0
        );
        const revenue = shop.revenue?.total || 0;
        const adSpend = shop.costs?.advertisement || 0;
        return {
          shopCode: shop.shopCode,
          domain: shop.domain,
          color: statisticsColors[shop.shopCode] || "#9ca3af",
          revenue,
          costs,
          profit: revenue - costs,
          margin: revenue ? ((revenue - costs) / revenue) * 100 : 0,
          roas: adSpend ? revenue / adSpend : 0,
          orders: shop.totalOrders || 0,
          raw: shop,
        };
      });
    },
  },
  methods: {
    formatEuro(value) {
      return `${getDecimalNumberString(value || 0, 2)} €`;
    },
    comparisonForShop(shopCode) {
      if (!this.comparisonData) return null;
      return this.comparisonData.find((shop) => shop.shopCode === shopCode) || null;
    },
  },
};
</script>

<style lang="scss" scoped>
.shops-table {
  border-radius: 12px !important;

  &__mobile {
    ::v-deep .v-expansion-panel {
      border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    ::v-deep .v-expansion-panel-header {
      padding: 10px 16px;
      min-height: 0;
    }
  }

  &__mobile-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-width: 0;
  }

  &__mobile-name {
    display: inline-flex;
    align-items: center;
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__mobile-stats {
    display: inline-flex;
    gap: 10px;
    flex-shrink: 0;
  }

  &__mobile-margin {
    color: rgba(0, 0, 0, 0.45);
  }

  &__mobile-content ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 4px 12px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    padding-bottom: 4px;
  }

  &__dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    margin-right: 8px;
  }

  &__name {
    font-weight: 600;
    font-size: 13px;
  }

  &__expand {
    padding: 12px !important;
    background: #f8f9fb;
  }
}

.num {
  font-variant-numeric: tabular-nums;
  font-size: 13px;

  &--good {
    color: #1b7f4d;
    font-weight: 600;
  }

  &--bad {
    color: #c23434;
    font-weight: 600;
  }
}
</style>
